import { getLayer } from './layerService.js';
import { getObjectSchema, getTopLevelPropertyKeys, fromWireFormatStates } from '../Hasp/properties/v2/wireFormat.js';

/**
 * Export all serializable properties from a node into a plain object.
 * Uses the openHASP export format (HEX6 colors + separate bg_opa).
 * Suitable for ObjectCreateCommand / ObjectDeleteCommand (used with importObject).
 */
export function captureNodeState(node) {
    const data = [];
    node.export(null, data, false);
    return data[0] ?? {};
}

// Keys captured separately via Konva geometry methods, not as plain properties.
const GEOM_KEYS = new Set(['x', 'y', 'w', 'h', 'width', 'height', 'size']);

/**
 * Capture ONLY the explicit style overrides this node currently stores —
 * NEVER resolved/inherited values (undo/redo must restore exactly what the
 * user had explicitly set, per StyleResolver.js's cascade — see
 * node.hasExplicitStyle()/node.resolveStyleTrace()). Mirrors node.parts'
 * own shape exactly: { [partName]: { states: { [stateName]: { properties } } } },
 * 'default' included alongside every other semantic state the node's schema
 * supports (node.getSupportedStates()) — this module never hardcodes state
 * names, it only asks the node.
 */
function captureExplicitPartStyles(node) {
    const styles = {};
    for (const partName of node.getPartNames()) {
        const propNames = Object.keys(node.getStyleDefinition(partName)?.properties ?? {});
        const statesOut = {};
        for (const stateName of ['default', ...node.getSupportedStates()]) {
            const properties = {};
            for (const propName of propNames) {
                if (!node.hasExplicitStyle(partName, propName, stateName)) continue;
                properties[propName] = stateName === 'default'
                    ? node.styleProp(partName, propName)
                    : node.stateStyleProp(partName, stateName, propName);
            }
            if (stateName === 'default' || Object.keys(properties).length > 0) {
                statesOut[stateName] = { properties };
            }
        }
        styles[partName] = { states: statesOut };
    }
    return styles;
}

/**
 * Capture the internal runtime state of a node for PropertyChangeCommand, in
 * the same shape the runtime model itself uses: top-level properties plus
 * every part's EXPLICIT style values (see captureExplicitPartStyles — never
 * resolved/inherited ones). Uses internal HEX8 color values (not the split
 * HEX6+opa export format) so they can be applied back via setStyle()/
 * setStateStyle() without reconstruction.
 */
export function capturePropertyState(node) {
    const state = {
        x: node.x(),
        y: node.y(),
        w: node.width(),
        h: node.height(),
        properties: {},
        styles: captureExplicitPartStyles(node),
    };
    for (const key of (node.properties ?? [])) {
        if (GEOM_KEYS.has(key)) continue;
        const val = node.getConfiguredProperty?.(key) ?? node[key];
        if (val !== undefined) state.properties[key] = val;
    }
    // hass_config is not a schema property so it is not in node.properties — capture explicitly
    if (node.hass_config !== undefined) {
        state.hass_config = JSON.parse(JSON.stringify(node.hass_config));
    }
    // Local GPIO bindings are a provider-specific object field, independent
    // from Home Assistant, but must travel with the same undo/redo snapshot.
    state.gpio_bindings = node.gpio_bindings !== undefined
        ? JSON.parse(JSON.stringify(node.gpio_bindings))
        : null;
    state.actions = JSON.parse(JSON.stringify(node.actions ?? {}));
    return state;
}

/**
 * Apply a property state captured by capturePropertyState back onto a live node.
 * - Top-level properties: plain Konva attrs, restored via node[key] + node.fire(key)
 *   to trigger their own listeners (unrelated to styles — always worked this way).
 * - Styles: restored through node.setStyle()/node.setStateStyle(), which handle the
 *   main bg_color/border_color rect special-case and onStyle() redraw
 *   notifications uniformly. Any property that's explicit on the node NOW but
 *   absent from the target snapshot is explicitly reset (node.resetStateStyle)
 *   rather than left alone — restoring exactly the target's explicit set, not
 *   a superset of it (see StyleResolver.js principle: explicit intent only).
 * Neither path triggers layerCallback while _historySuppress is set, so no
 * spurious history entries are created.
 */
export function applyPropertyState(node, state) {
    if (!node || !state) return;

    // Geometry
    if (state.x !== undefined) node.x(state.x);
    if (state.y !== undefined) node.y(state.y);
    const w = state.w;
    const h = state.h;
    if (w !== undefined || h !== undefined) {
        node.setNewSize?.({
            width:  w ?? node.width(),
            height: h ?? node.height(),
        });
    }
    node.setObjectSize?.();

    const layer = node.getLayer?.();
    if (layer) layer._historySuppress = true;

    try {
        for (const [key, value] of Object.entries(state.properties ?? {})) {
            if (GEOM_KEYS.has(key)) continue;
            node[key] = value;
            node.fire(key);
        }

        for (const partName of node.getPartNames()) {
            const propNames = Object.keys(node.getStyleDefinition(partName)?.properties ?? {});
            const targetStates = state.styles?.[partName]?.states ?? {};
            for (const stateName of ['default', ...node.getSupportedStates()]) {
                const targetProps = targetStates[stateName]?.properties ?? {};
                for (const propName of propNames) {
                    if (propName in targetProps) continue;
                    if (node.hasExplicitStyle(partName, propName, stateName)) {
                        node.resetStateStyle(partName, stateName, propName);
                    }
                }
                for (const [propName, value] of Object.entries(targetProps)) {
                    if (stateName === 'default') node.setStyle(partName, propName, value);
                    else node.setStateStyle(partName, stateName, propName, value);
                }
            }
        }

        // hass_config — not a schema property, restored directly (no visual update needed)
        if (state.hass_config !== undefined) {
            node.hass_config = JSON.parse(JSON.stringify(state.hass_config));
        }
        if (state.gpio_bindings !== undefined) {
            if (state.gpio_bindings === null) delete node.gpio_bindings;
            else node.gpio_bindings = JSON.parse(JSON.stringify(state.gpio_bindings));
        }
        node.actions = JSON.parse(JSON.stringify(state.actions ?? {}));
    } finally {
        if (layer) layer._historySuppress = false;
        layer?.batchDraw();
    }
}

/**
 * Convert a flat, wire-keyed config — e.g. ObjectCreateCommand's stored
 * creation config, itself a JSONL-shaped object handed to layer.importObject()
 * — into the same {x, y, w, h, properties, styles} shape capturePropertyState
 * produces, so RestoreObjectVersionCommand can treat both origins uniformly.
 * fromWireFormatStates is the OpenHASP import adapter's translation function
 * (see wireFormat.js) — this is the only place besides Importer.js/
 * ConfigManager.js allowed to call it. Its output shape already matches
 * capturePropertyState's styles convention exactly (states, including
 * 'default', nested under each part) — no reshaping needed.
 */
export function wireConfigToPropertyState(config) {
    const schema = getObjectSchema(config.obj);
    if (!schema) return { properties: { ...config } };

    const state = {
        x: config.x, y: config.y, w: config.width, h: config.height,
        properties: {},
        styles: fromWireFormatStates(schema, config),
    };
    for (const key of getTopLevelPropertyKeys(schema)) {
        if (GEOM_KEYS.has(key)) continue;
        if (config[key] !== undefined) state.properties[key] = config[key];
    }
    if (config.hass_config !== undefined) {
        state.hass_config = JSON.parse(JSON.stringify(config.hass_config));
    }
    state.gpio_bindings = config.gpio_bindings !== undefined
        ? JSON.parse(JSON.stringify(config.gpio_bindings))
        : null;
    return state;
}

export function getNode(uuid) {
    return getLayer()?.findOne('#' + uuid) ?? null;
}
